import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';

import FilePDFIcon from '../../assets/icons/FilePDF';
import FilePDFGrayIcon from '../../assets/icons/FilePDFGray';
import { useGetRequestPdfDownloadUrl } from '../../hooks/api/request-pcf';
import useNotificationContext from '../../hooks/notification/useNotificationContext';
import { GetDownloadPDFUrlErrorCodes } from '../../shared/enums/get-download-pdf-error-code';
import { ToastSeverity } from '../../shared/enums/toast-severity';

export type DownloadPdfFileButtonProps = {
  className?: string;
  requestId?: number;
  isDisabled?: boolean;
};

const DownloadPdfFileButton = ({
  className,
  requestId,
  isDisabled,
}: DownloadPdfFileButtonProps) => {
  const { notify } = useNotificationContext();
  const { refetch: fetchPDFDownloadUrl } = useGetRequestPdfDownloadUrl(requestId, {
    retry: false,
  });

  const queryClient = useQueryClient();

  const handleClick = async () => {
    if (isDisabled) {
      return;
    }

    const { data, isSuccess, isError, error } = await fetchPDFDownloadUrl();

    const errorCode = (error as Error)?.message;
    const isPdfNotFound = Object.values(GetDownloadPDFUrlErrorCodes).includes(
      errorCode as GetDownloadPDFUrlErrorCodes,
    );

    const baseErrorTranslationKey = isPdfNotFound
      ? 'toastMessages.downloadPdf.states.failure.pdfNotFound'
      : 'toastMessages.downloadPdf.states.failure';

    if (isError || !data?.downloadUrl) {
      notify({
        severity: ToastSeverity.ERROR,
        summary: t(`${baseErrorTranslationKey}.summary`),
        detail: t(`${baseErrorTranslationKey}.detail`),
        life: 3000,
      });
    }

    if (isSuccess && data.downloadUrl) {
      window.open(data.downloadUrl, '_blank');

      // Invalidate components list since opening PDF file will update the status to completed
      queryClient.invalidateQueries({ queryKey: ['components'] });
    }
  };

  const { t } = useTranslation();

  return (
    <div>
      {isDisabled && (
        <Tooltip
          target={`.pdf-download-btn-${requestId}`}
          position='top'
          autoHide={false}
          baseZIndex={1}
          pt={{ root: { className: 'pointer-events-auto' } }}
        >
          <span>{t('componentsPage.pdfDownloadDisabledTooltip')}</span>
        </Tooltip>
      )}
      <Button
        className={classNames(
          `p-0 pdf-download-btn-${requestId}`,
          {
            'cursor-pointer': !isDisabled,
            'cursor-auto': isDisabled,
          },
          className,
        )}
        icon={isDisabled ? FilePDFGrayIcon : FilePDFIcon}
        link
        onClick={handleClick}
      />
    </div>
  );
};

export default DownloadPdfFileButton;
